<template>
  <BittsCollapse
    v-model:is-open="isOpen"
    chevron-placement="right"
    :toggle-on-body-click="false"
    class="px-12 py-8 bg-neutral-50/50 rounded-lg"
  >
    <template #title>
      <div class="flex items justify-between w-full">
        <h6 class="text-info-text text-base/6 font-bold">
          Partner Populations
        </h6>
        <p class="text-neutral-text-weak text-base/6 font-normal">{{
          isOpen ? 'Collapse' : 'Expand'
        }}</p>
      </div>
    </template>
    <div class="pt-12 pl-16">
      <p v-if="hideOfflinePartners" class="text-neutral-text text-sm mb-8">
        Partner populations appear only when the partner's CRM is synced and the
        required fields are shared.
      </p>
      <h6 class="text-neutral-text-strong text-base/6 font-bold">
        Default settings
      </h6>
      <p class="text-neutral-text text-sm"
        >Editing this setting will affect the partners below</p
      >
      <PartnerPopulationsStandardSettings
        :integration-profile-settings="integrationProfileSettings"
        :is-disabled
        :setting-type="settingType"
        @update-settings="updateDefaultPartnerSettings"
      />
      <IntegrationSettingsPartnerDefaultsSwitch
        v-if="settingType !== 'webhook'"
        class="my-16"
        :is-checked="
          !integrationProfileSettings?.exclude_partnerships_created_since
        "
        :is-disabled
        @update:exclude-partnerships-created-since="
          updateExcludePartnershipsCreatedSince
        "
      />
      <IntegrationSettingsPartnerSpecificTree
        :integration-profile-settings="integrationProfileSettings"
        :is-disabled
        :setting-type="settingType"
        :hide-offline-partners="hideOfflinePartners"
        @update-settings="updatePartnerOverrides"
      />
    </div>
  </BittsCollapse>
</template>

<script setup lang="ts">
import { BittsCollapse } from '@crossbeam/bitts';

import { ref } from 'vue';

import { MinimalIntegrationProfile } from '@/types/integration_profiles';

import IntegrationSettingsPartnerDefaultsSwitch from './IntegrationSettingsPartnerDefaultsSwitch.vue';
import IntegrationSettingsPartnerSpecificTree from './IntegrationSettingsPartnerSpecificTree.vue';
import PartnerPopulationsStandardSettings from './IntegrationSettingsPartnerStandardPopulations.vue';

withDefaults(
  defineProps<{
    integrationProfileSettings?: MinimalIntegrationProfile | null;
    isDisabled?: boolean;
    settingType: string;
    hideOfflinePartners?: boolean;
  }>(),
  {
    integrationProfileSettings: undefined,
    isDisabled: false,
    hideOfflinePartners: false,
  },
);
const emit = defineEmits<{
  (
    e: 'update:partner-default-settings' | 'update:partner-overrides',
    payload: MinimalIntegrationProfile,
  ): void;
  (e: 'update:exclude-partnerships-created-since', isChecked: boolean): void;
}>();

const isOpen = ref(true);

const updateDefaultPartnerSettings = (payload: MinimalIntegrationProfile) => {
  emit('update:partner-default-settings', payload);
};
const updatePartnerOverrides = (payload: MinimalIntegrationProfile) => {
  emit('update:partner-overrides', payload);
};
const updateExcludePartnershipsCreatedSince = (isChecked: boolean) => {
  emit('update:exclude-partnerships-created-since', isChecked);
};
</script>
